import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useBatches } from '@/hooks/useBatches'
import { useEnrollInCourse } from '@/hooks/useEnrollments'
import { useCourse } from '@/hooks/useCourses'
import { BatchCheckoutModal } from '@/components/checkout/BatchCheckoutModal'
import type { Batch } from '@/types/batch'

export const BatchesPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error } = useBatches(courseId || '')
  const { data: course } = useCourse(courseId || '')
  const enrollMutation = useEnrollInCourse()

  const [enrollingBatchId, setEnrollingBatchId] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [checkoutBatch, setCheckoutBatch] = useState<Batch | null>(null)

  const batches: Batch[] = (data?.data || []).filter((b: Batch) => b.status === 'active')

  const isRegistrationClosed = (batch: Batch): boolean => {
    if (!batch.registrationDeadline) return false
    return new Date(batch.registrationDeadline) < new Date()
  }

  const isBatchExpired = (batch: Batch): boolean => {
    return new Date(batch.endDate) < new Date()
  }

  const isFull = (batch: Batch): boolean => {
    return (batch.enrolledCount || 0) >= batch.capacity
  }

  const canEnroll = (batch: Batch): boolean => {
    return !isFull(batch) && !isRegistrationClosed(batch) && !isBatchExpired(batch)
  }

  const getDisabledReason = (batch: Batch): string | null => {
    if (isBatchExpired(batch)) return 'Batch sudah selesai'
    if (isRegistrationClosed(batch)) return 'Registrasi ditutup'
    if (isFull(batch)) return 'Penuh'
    return null
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount)

  const handleSelectBatch = async (batch: Batch) => {
    setErrorMsg(null)

    // Batch berbayar → buka checkout modal
    if (batch.batchFee > 0) {
      setCheckoutBatch(batch)
      return
    }

    // Batch gratis → enroll langsung
    setEnrollingBatchId(batch.id)
    try {
      const result = await enrollMutation.mutateAsync({ courseId: courseId!, batchId: batch.id })
      if (result.data) {
        navigate(`/learn/${result.data.id}`)
      }
    } catch (err: any) {
      const code = err?.response?.data?.error?.code
      const message = err?.response?.data?.error?.message

      if (code === 'KTP_NOT_VERIFIED') {
        setErrorMsg('KTP kamu belum diverifikasi. Harap lengkapi verifikasi KTP di halaman Profil terlebih dahulu.')
      } else if (code === 'BATCH_FULL') {
        setErrorMsg(`Batch "${batch.title}" sudah penuh. Silakan pilih batch lain.`)
      } else if (code === 'BATCH_NOT_AVAILABLE') {
        setErrorMsg(`Batch "${batch.title}" tidak tersedia untuk pendaftaran saat ini.`)
      } else if (code === 'ALREADY_ENROLLED') {
        setErrorMsg('Kamu sudah terdaftar di course ini.')
      } else {
        setErrorMsg(message || 'Gagal mendaftar ke batch. Silakan coba lagi.')
      }
    } finally {
      setEnrollingBatchId(null)
    }
  }

  if (!courseId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Course tidak ditemukan.</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-24 md:pb-12">
      {/* Checkout Modal untuk batch berbayar */}
      {checkoutBatch && course && (
        <BatchCheckoutModal
          isOpen={!!checkoutBatch}
          onClose={() => setCheckoutBatch(null)}
          batch={{ id: checkoutBatch.id, title: checkoutBatch.title, batchFee: checkoutBatch.batchFee, registrationDeadline: checkoutBatch.registrationDeadline, endDate: checkoutBatch.endDate }}
          courseId={courseId!}
          courseName={course.title}
        />
      )}

      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-white/10 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => courseId ? navigate(`/course/${courseId}`) : navigate(-1)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 transition-colors text-slate-600 dark:text-slate-300"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            <span className="text-sm font-semibold">Kembali</span>
          </button>
          <div>
            <h1 className="text-base font-black text-slate-900 dark:text-white">Pilih Jadwal Batch</h1>
            {course?.title && (
              <p className="text-xs text-slate-500 dark:text-slate-400">{course.title}</p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Error banner */}
        {errorMsg && (
          <div className="flex items-start gap-3 p-4 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 rounded-2xl">
            <span className="material-symbols-outlined text-rose-500 text-[20px] mt-0.5">error</span>
            <div className="flex-1">
              <p className="text-sm font-bold text-rose-700 dark:text-rose-400">{errorMsg}</p>
              {errorMsg.includes('KTP') && (
                <button
                  onClick={() => navigate('/profile')}
                  className="mt-1 text-xs font-bold text-rose-600 dark:text-rose-400 underline hover:opacity-70"
                >
                  Pergi ke Profil untuk verifikasi KTP →
                </button>
              )}
            </div>
            <button onClick={() => setErrorMsg(null)} className="text-rose-400 hover:text-rose-600">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-white dark:bg-slate-900 rounded-2xl animate-pulse" />
            ))}
          </div>
        )}

        {/* Error fetch */}
        {error && !isLoading && (
          <div className="text-center py-16">
            <span className="material-symbols-outlined text-4xl text-slate-400 mb-3 block">error</span>
            <p className="text-slate-500 dark:text-slate-400">Gagal memuat daftar batch.</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && batches.length === 0 && (
          <div className="text-center py-16">
            <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600 mb-4 block">calendar_month</span>
            <h3 className="text-base font-black text-slate-700 dark:text-slate-300 mb-1">Belum Ada Batch Tersedia</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Jadwal batch untuk course ini belum dibuka. Coba lagi nanti.</p>
          </div>
        )}

        {/* All batches expired/full state */}
        {!isLoading && !error && batches.length > 0 && batches.every(b => !canEnroll(b)) && (
          <div className="text-center py-16">
            <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600 mb-4 block">event_busy</span>
            <h3 className="text-base font-black text-slate-700 dark:text-slate-300 mb-1">Belum Ada Batch Yang Bisa Didaftar</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Semua batch untuk course ini sudah penuh atau registrasi sudah ditutup.</p>
          </div>
        )}

        {/* Batch list */}
        {!isLoading && batches.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
              {batches.length} jadwal — {batches.filter(b => canEnroll(b)).length} bisa didaftar
            </p>
            {batches.map(batch => {
              const enrolledCount = batch.enrolledCount || 0
              const spotsLeft = batch.capacity - enrolledCount
              const batchIsFull = isFull(batch)
              const batchRegClosed = isRegistrationClosed(batch)
              const batchExpired = isBatchExpired(batch)
              const batchCanEnroll = canEnroll(batch)
              const disabledReason = getDisabledReason(batch)
              const isEnrolling = enrollingBatchId === batch.id
              const isPaid = batch.batchFee > 0

              return (
                <div
                  key={batch.id}
                  className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-white/10 p-5 shadow-sm"
                >
                  {/* Status badges */}
                  {(batchExpired || batchRegClosed || batchIsFull) && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {batchExpired && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20">
                          <span className="material-symbols-outlined text-[14px]">event_busy</span>
                          Batch Selesai
                        </span>
                      )}
                      {!batchExpired && batchRegClosed && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
                          <span className="material-symbols-outlined text-[14px]">schedule</span>
                          Registrasi Ditutup
                        </span>
                      )}
                      {!batchExpired && !batchRegClosed && batchIsFull && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20">
                          <span className="material-symbols-outlined text-[14px]">group_off</span>
                          Penuh
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-black text-slate-900 dark:text-white text-base truncate">{batch.title}</h3>
                      {batch.description && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{batch.description}</p>
                      )}

                      <div className="flex flex-wrap gap-3 mt-3">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                          <span className="material-symbols-outlined text-[16px]">calendar_month</span>
                          <span>
                            {new Date(batch.startDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {' — '}
                            {new Date(batch.endDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        {/* Registration deadline */}
                        {batch.registrationDeadline && (
                          <div className={`flex items-center gap-1.5 text-xs font-bold ${batchRegClosed ? 'text-amber-500' : 'text-slate-500 dark:text-slate-400'}`}>
                            <span className="material-symbols-outlined text-[16px]">timer</span>
                            <span>
                              {batchRegClosed ? 'Deadline lewat' : `Deadline: ${new Date(batch.registrationDeadline).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                            </span>
                          </div>
                        )}
                        <div className={`flex items-center gap-1.5 text-xs font-bold ${batchIsFull ? 'text-rose-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          <span className="material-symbols-outlined text-[16px]">group</span>
                          <span>{batchIsFull ? 'Penuh' : `${spotsLeft} slot tersisa dari ${batch.capacity}`}</span>
                        </div>
                        {/* Harga batch */}
                        <div className={`flex items-center gap-1.5 text-xs font-black ${isPaid ? 'text-slate-900 dark:text-white' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          <span className="material-symbols-outlined text-[16px]">payments</span>
                          <span>{isPaid ? formatCurrency(batch.batchFee) : 'Gratis'}</span>
                        </div>
                        {/* Lokasi */}
                        {batch.location && (
                          <div className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400 w-full">
                            <span className="material-symbols-outlined text-[16px] shrink-0 mt-0.5">location_on</span>
                            <span className="line-clamp-2">{batch.location}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => batchCanEnroll && handleSelectBatch(batch)}
                      disabled={!batchCanEnroll || isEnrolling || enrollMutation.isPending}
                      title={disabledReason || undefined}
                      className={`shrink-0 px-5 py-2.5 rounded-xl text-sm font-black transition-all whitespace-nowrap ${
                        !batchCanEnroll
                          ? 'bg-slate-100 dark:bg-white/5 text-slate-400 cursor-not-allowed'
                          : isPaid
                          ? 'bg-primary text-white hover:scale-[1.03] active:scale-[0.97] shadow-lg shadow-primary/20 disabled:opacity-60'
                          : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:scale-[1.03] active:scale-[0.97] shadow-lg shadow-slate-200 dark:shadow-none disabled:opacity-60'
                      }`}
                    >
                      {isEnrolling ? (
                        <span className="flex items-center gap-1.5">
                          <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          Mendaftar...
                        </span>
                      ) : disabledReason || (isPaid ? 'Daftar & Bayar' : 'Daftar Gratis')}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
